import { AxiosRequestConfig, AxiosResponse } from 'axios';
import queryString from 'query-string';

import { userServiceApi } from '~src/services/ApiController';
import logger from '~src/services/logger/LlsLogger';
import { PaginationI } from '~src/interfaces/pagination.interface';
import { IntegrationTypes } from '~src/modules/companySettings/importUsers/types';

export const fetchIntegrations = () => userServiceApi.get<ImportIntegration[]>('/integrations')

export const fetchImportsStatus = (queryObj?: ImportsStatusQueryI, config?: AxiosRequestConfig) => {
  const query = queryObj ? queryString.stringify(queryObj) : '';
  return userServiceApi.get<ImportsStatusResp>(`/imports?${query}`, config);
};

export const postImport = (integrationId: string) =>
  userServiceApi.post('/imports', { integration_id: integrationId });

export const updateIntegrationConfig = (integrationId: string, payload: ConfigSubmitObj) =>
  userServiceApi.put<ConfigSubmitObj, AxiosResponse<ImportIntegration>>(`/integrations/${integrationId}`, payload);

export const activateIntegration = (configSubmitObj: ConfigSubmitObj) =>
  userServiceApi.post<ConfigSubmitObj, AxiosResponse<ImportIntegration>>('/integrations', configSubmitObj);

export const fetchPreviewList = (queryObj: PreviewListQueryI) => {
  const query = queryString.stringify(queryObj);
  return userServiceApi.get<UserImportPreviewResp>(`/import-items?${query}`);
};

export const postToggleIncludeUser = (id: string, isInclude: boolean) =>
  userServiceApi.post(`/import-items/${id}`, { execute: isInclude });

export const postChangeImportState = (id: string, props: ChangeImportStateParamsI) =>
  userServiceApi.post(`/imports/${id}`, props);

export const fetchImportStatus = (id: string, queryObj?: ImportStatusQueryI, config?: AxiosRequestConfig) => {
  const query = queryObj ? queryString.stringify(queryObj) : '';
  return userServiceApi.get<ImportStatusResp>(`/imports/${id}?${query}`, config);
};

export const cancelImport = (id: string) => userServiceApi.delete(`/imports/${id}`);

export const logError = (msg: string, err: string, file: string) => {
  logger.info(msg, {
    error: new Error(err),
    info: { componentStack: file },
  });
};

export interface ConfigSubmitObj {
  name?: IntegrationTypes;
  config: ConfigDataI;
  auto_import: boolean
}

export type ConfigDataI = Record<string, string>;

export interface ImportIntegration {
  id: string;
  name: string;
  config: ConfigDataI;
  auto_import: boolean;
}

export type ImportStatusFilters =
  | 'pending'
  | 'transforming'
  | 'transformed'
  | 'preparing'
  | 'prepared'
  | 'executing'
  | 'failed'
  | 'successful'
  | 'canceled';

export interface ImportsStatusQueryI {
  'filter.running'?: boolean;
  'filter.status'?: ImportStatusFilters;
}

export interface ImportStatusQueryI {
  include?: 'item_stats';
}

export interface ImportsStatusResp {
  pagination: PaginationI;
  filters: {
    [key in ImportStatusFilters]: string[];
  };
  sorting: Array<{
    field: number;
    value: string;
  }>;
  include: string[];
  items: ImportStatusItemI[];
}

interface ImportStatusItemI {
  id: string;
  integration_id: string;
  status: ImportStatusFilters;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  progress: number;
  created_count: number;
  error: string;
  error_count: number;
  updated_count: number;
}

export interface ImportStatusResp {
  id: string;
  integration_id: string;
  status: string;
  error: string;
  estimated_item_count: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  stats: Partial<{
    progress: number;
    failed_items: number;
    successful_items: number;
  }>;
}

export interface UserImportPreviewResp {
  pagination: PaginationI;
  filters: { import: string[]; preview: string[] };
  include: string[];
  sorting: Array<{ field: number; value: string }>;
  items: ImportPreviewItemI[];
}

export interface ImportPreviewItemI {
  actions?: PreviewItemAction[];
  id: string;
  import_id: string;
  integration_user_id: string;
  execute: boolean;
  errors?: string[];
  import_data: ImportDataI;
  existing_data?: ExistingDataI;
  created_at: string;
  updated_at: string;
}

interface PreviewItemAction {
  id: string;
  import_item_id: string;
  import_data: ItemImportData;
  status: string;
  type: string;
  created_at: string;
  updated_at: string;
}

type ItemImportData = Record<
  string,
  {
    current: string | boolean | null;
    new: string | boolean;
  }
>;

export interface CoreUserData {
  firstname: string;
  lastname: string;
  email: string;
  is_admin: boolean;
  username?: string;
}

export interface ImportDataI extends CoreUserData {
  integration_user_id: string;
  attributes: Record<string, any>;
  transform_errors: any[];
}

export interface ExistingDataI extends CoreUserData {
  uuid: string;
  attributes: Record<string, any>;
}

export interface PreviewListQueryI {
  page?: number;
  per_page?: number;
  include?: 'actions';
  'filter.import'?: string;
  'filter.has_actions'?: boolean;
  'filter.preview'?: boolean;
  'filter.has_errors'?: boolean;
  sort?: 'has_errors';
}

export interface ChangeImportStateParamsI {
  check_all?: boolean;
  confirmed?: boolean;
}
